import type { ClientOption, ParentTicketFormValues } from '@/types/ticket'
import type { FEDispatchSelection } from '@/components/forms/parentTicket/FEDispatchFlow'
import type { FreeTextLocationResult } from '@/types/location'

export function normalizeStoreId(value: unknown): number | null {
  if (value == null || value === '') return null
  const n = Number(value)
  return Number.isFinite(n) ? n : null
}

export function storesMatch(
  leftStoreId: unknown,
  leftStoreCode: string | undefined | null,
  rightStoreId: unknown,
  rightStoreCode: string | undefined | null,
): boolean {
  const leftId = normalizeStoreId(leftStoreId)
  const rightId = normalizeStoreId(rightStoreId)
  if (leftId == null || rightId == null) return false
  return leftId === rightId && (leftStoreCode ?? '').trim() === (rightStoreCode ?? '').trim()
}

export function patchSelectionWithStore(
  selection: FEDispatchSelection,
  store: ClientOption,
): FEDispatchSelection {
  const storeId = Number(store.storeId)
  const storeCode = store.storeCode ?? store.branchCode ?? selection.storeCode ?? ''

  return {
    ...selection,
    locationType: 'store',
    storeId: Number.isFinite(storeId) ? storeId : selection.storeId,
    storeCode,
    storeName: store.storeName ?? selection.storeName,
    storeAddress:
      store.branchAddress ?? store.locationAddress ?? store.normalizedAddress ?? selection.storeAddress,
    latitude: store.latitude ?? selection.latitude,
    longitude: store.longitude ?? selection.longitude,
    incidentLocationMunicipality: null,
    incidentLocationProvince: null,
    incidentLocationCountry: null,
  }
}

export function patchSelectionWithLocation(
  selection: FEDispatchSelection,
  location: FreeTextLocationResult,
): FEDispatchSelection {
  return {
    ...selection,
    locationType: 'location',
    storeId: null,
    storeCode: '',
    storeName: location.display_name,
    storeAddress: location.display_name,
    latitude: location.latitude,
    longitude: location.longitude,
    locationDisplayName: location.display_name,
    incidentLocationMunicipality: location.municipality ?? null,
    incidentLocationProvince: location.province ?? null,
    incidentLocationCountry: location.country ?? null,
  }
}

export function incidentLocationFieldsFromLocation(
  location: Pick<
    FreeTextLocationResult,
    'display_name' | 'latitude' | 'longitude' | 'place_id' | 'municipality' | 'province' | 'country' | 'source'
  >,
): Pick<
  ParentTicketFormValues,
  | 'incidentLocationType'
  | 'incidentLocationName'
  | 'incidentLocationAddress'
  | 'incidentLocationLatitude'
  | 'incidentLocationLongitude'
  | 'incidentLocationPlaceId'
  | 'incidentLocationSource'
  | 'incidentLocationMunicipality'
  | 'incidentLocationProvince'
  | 'incidentLocationCountry'
> {
  return {
    incidentLocationType: 'location',
    incidentLocationName: location.display_name,
    incidentLocationAddress: location.display_name,
    incidentLocationLatitude: location.latitude,
    incidentLocationLongitude: location.longitude,
    incidentLocationPlaceId:
      location.place_id != null && location.place_id !== ''
        ? String(location.place_id)
        : null,
    incidentLocationSource: location.source ?? 'nominatim',
    incidentLocationMunicipality: location.municipality ?? null,
    incidentLocationProvince: location.province ?? null,
    incidentLocationCountry: location.country ?? null,
  }
}

/** @deprecated Prefer incidentLocationFieldsFromLocation — kept for existing call sites. */
export function incidentLocationFieldsFromNominatim(
  location: Pick<
    FreeTextLocationResult,
    'display_name' | 'latitude' | 'longitude' | 'place_id' | 'municipality' | 'province' | 'country' | 'source'
  >,
): ReturnType<typeof incidentLocationFieldsFromLocation> {
  return incidentLocationFieldsFromLocation({
    ...location,
    source: location.source ?? 'nominatim',
  })
}

export function clearIncidentLocationGeoFields(): Pick<
  ParentTicketFormValues,
  'incidentLocationMunicipality' | 'incidentLocationProvince' | 'incidentLocationCountry'
> {
  return {
    incidentLocationMunicipality: null,
    incidentLocationProvince: null,
    incidentLocationCountry: null,
  }
}

export function storeFieldsFromClientOption(
  store: ClientOption,
): Pick<
  ParentTicketFormValues,
  | 'storeId'
  | 'storeCode'
  | 'incidentLocationType'
  | 'incidentLocationName'
  | 'incidentLocationAddress'
  | 'incidentLocationLatitude'
  | 'incidentLocationLongitude'
  | 'incidentLocationSource'
  | 'incidentLocationMunicipality'
  | 'incidentLocationProvince'
  | 'incidentLocationCountry'
> {
  return {
    storeId: store.storeId,
    storeCode: store.storeCode ?? store.branchCode ?? '',
    incidentLocationType: 'store',
    incidentLocationName: store.storeName,
    incidentLocationAddress:
      store.branchAddress ?? store.locationAddress ?? store.normalizedAddress ?? store.location?.address ?? '',
    incidentLocationLatitude: store.latitude ?? store.location?.latitude ?? null,
    incidentLocationLongitude: store.longitude ?? store.location?.longitude ?? null,
    incidentLocationSource: 'store',
    incidentLocationMunicipality: null,
    incidentLocationProvince: null,
    incidentLocationCountry: null,
  }
}

export type ReferenceLocationForDispatch = {
  type: 'store' | 'location'
  name: string
  code?: string | null
  address?: string | null
  latitude?: number | string | null
  longitude?: number | string | null
  contactNumber?: string | null
  location?: {
    municipality?: string | null
    province?: string | null
    country?: string | null
  } | null
}

export type DispatchWindow = {
  scheduleDate: string
  scheduleTime: string
  scheduleEndTime: string
  spansMultipleDays?: boolean
  scheduleEndDate?: string
}

/**
 * Canonical builder for a live FE dispatch selection (confirm-from-recommender path).
 * Ticket-edit hydration stays in feDispatchSelectionFromTicket.ts because it must
 * reconstruct from persisted fe_schedules + engineer meta, not a live reference location.
 */
export function buildLiveFEDispatchSelection(params: {
  reference: ReferenceLocationForDispatch
  storeId: number | null
  feUserId: number
  feName: string
  dispatchWindow: DispatchWindow
  contactNumberOverride?: string
  role: 'primary' | 'additional' | 'secondary'
  additionalSlot?: number
  locationSource?: string | null
}): FEDispatchSelection {
  const { reference, storeId, feUserId, feName, dispatchWindow, contactNumberOverride, role } = params
  const additionalSlot = params.additionalSlot
  const normalizedRole = role === 'primary' ? 'primary' : 'additional'
  const lat =
    reference.latitude != null && Number.isFinite(Number(reference.latitude))
      ? Number(reference.latitude)
      : null
  const lng =
    reference.longitude != null && Number.isFinite(Number(reference.longitude))
      ? Number(reference.longitude)
      : null

  return {
    locationType: reference.type,
    storeId: reference.type === 'store' ? storeId : null,
    storeCode: reference.type === 'store' ? reference.code ?? '' : '',
    storeName: reference.name,
    storeAddress: reference.address ?? '',
    latitude: lat,
    longitude: lng,
    locationDisplayName: reference.type === 'location' ? reference.name : undefined,
    locationSource:
      params.locationSource ??
      (reference.type === 'location' ? 'nominatim' : 'store'),
    incidentLocationMunicipality:
      reference.type === 'location' ? reference.location?.municipality ?? null : null,
    incidentLocationProvince:
      reference.type === 'location' ? reference.location?.province ?? null : null,
    incidentLocationCountry:
      reference.type === 'location' ? reference.location?.country ?? null : null,
    feUserId,
    feName,
    scheduleDate: dispatchWindow.scheduleDate,
    scheduleTime: dispatchWindow.scheduleTime,
    scheduleEndTime: dispatchWindow.scheduleEndTime,
    contactNumberOverride,
    spansMultipleDays: dispatchWindow.spansMultipleDays ?? false,
    scheduleEndDate: dispatchWindow.scheduleEndDate,
    role: normalizedRole,
    additionalSlot: normalizedRole === 'additional' ? additionalSlot : undefined,
  }
}
